// Reception Dashboard — Phase 1 hotel operational stack
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users, LogIn, LogOut, RefreshCw, Loader2, Plus, X, Check, DollarSign,
  Coffee, Utensils, Car, Sparkles, AlertCircle, Wallet, TrendingUp, Download, FileText, MessageCircle, Package, Camera, Smartphone
} from 'lucide-react';
import PushNotificationButton from './PushNotificationButton';
import EvizitorPrecheckModal, { fetchEvizitorPrecheck } from './EvizitorPrecheckModal';
import Beds24Feed from './Beds24Feed';
import BriefingCard from './BriefingCard';
import RoomMoveModal from './RoomMoveModal';
import GuestMessageModal from './GuestMessageModal';
import LostFoundPanel from './LostFoundPanel';
import GuestMemoryCard from './GuestMemoryCard';
import { apiCall } from './_shared';
import AutoMessagesCard from './AutoMessagesCard';
import IdScanPanel from './IdScanPanel';
import FolioGuests from './FolioGuests';
import FolioMemory from './FolioMemory';
import CashFloatBanner from './CashFloatBanner';
import ShiftCloseModal from './ShiftCloseModal';
import ShiftPanel from './ShiftPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Staff-facing UI copy — English / Crnogorski / Shqip
const T = {
  en: {
    today: "Today", arrivals: "Arrivals", departures: "Departures", inHouse: "In-house",
    revenue: "Daily revenue", refresh: "Refresh", checkIn: "Check in", checkOut: "Check out",
    openFolio: "Open folio", noArrivals: "No arrivals today", noDepartures: "No departures today",
    noInHouse: "No guests currently in-house", room: "Room", guests: "Guests", pax: "pax",
    balance: "Balance", charges: "Charges", payments: "Payments", addCharge: "Add charge",
    addPayment: "Add payment", close: "Close", passport: "Passport #", country: "Country",
    tourist_tax: "Tourist tax (€)", notes: "Notes", confirm: "Confirm check-in", complete: "Complete check-out",
    charge: "Charge", payment: "Payment", item: "Item", amount: "Amount", method: "Method",
    cash: "Cash", card: "Card", transfer: "Transfer", beds24: "Beds24 (online)", save: "Save",
    quickChargeDinner: "Dinner", quickChargeBreakfast: "Breakfast", quickChargeTransfer: "Airport transfer (€80)",
    quickChargeLaundry: "Laundry (€7)", quickChargeMinibar: "Minibar", quickChargeExtraBed: "Extra bed",
    category: "Category", extra: "Extra", food: "Food & Beverage", vat: "VAT", vatBreakdown: "VAT breakdown",
    totals: "Totals", checkedIn: "Checked in", checkedOut: "Checked out", reserved: "Reserved",
    initFolio: "Initialize folio to start", cashDrawer: "Cash today", cardsToday: "Cards today",
    totalReceived: "Total received today", chargesToday: "Charges today",
    exportEvizitor: "eVizitor CSV", downloadInvoice: "Invoice PDF",
  },
  me: {
    today: "Danas", arrivals: "Dolasci", departures: "Odlasci", inHouse: "Trenutno u hotelu",
    revenue: "Dnevni prihod", refresh: "Osvježi", checkIn: "Prijava", checkOut: "Odjava",
    openFolio: "Otvori račun", noArrivals: "Nema dolazaka danas", noDepartures: "Nema odlazaka danas",
    noInHouse: "Nema gostiju u hotelu", room: "Soba", guests: "Gosti", pax: "os.",
    balance: "Stanje", charges: "Stavke", payments: "Uplate", addCharge: "Dodaj stavku",
    addPayment: "Dodaj uplatu", close: "Zatvori", passport: "Pasoš", country: "Zemlja",
    tourist_tax: "Boravišna taksa (€)", notes: "Napomene", confirm: "Potvrdi prijavu", complete: "Završi odjavu",
    charge: "Stavka", payment: "Uplata", item: "Naziv", amount: "Iznos", method: "Način",
    cash: "Gotovina", card: "Kartica", transfer: "Uplata", beds24: "Beds24 (online)", save: "Sačuvaj",
    quickChargeDinner: "Večera", quickChargeBreakfast: "Doručak", quickChargeTransfer: "Aerodromski transfer (€80)",
    quickChargeLaundry: "Pranje (€7)", quickChargeMinibar: "Mini-bar", quickChargeExtraBed: "Dodatni krevet",
    category: "Kategorija", extra: "Dodatno", food: "Hrana i piće", vat: "PDV", vatBreakdown: "PDV pregled",
    totals: "Ukupno", checkedIn: "Prijavljen", checkedOut: "Odjavljen", reserved: "Rezervisano",
    initFolio: "Inicijalizuj račun za početak", cashDrawer: "Gotovina danas", cardsToday: "Kartice danas",
    totalReceived: "Ukupno naplaćeno danas", chargesToday: "Stavke danas",
    exportEvizitor: "eVizitor CSV", downloadInvoice: "Račun PDF",
  },
  sq: {
    today: "Sot", arrivals: "Mbërritjet", departures: "Nisjet", inHouse: "Në hotel tani",
    revenue: "Të ardhurat sot", refresh: "Rifresko", checkIn: "Regjistro", checkOut: "Largim",
    openFolio: "Hap faturën", noArrivals: "Nuk ka mbërritje sot", noDepartures: "Nuk ka nisje sot",
    noInHouse: "Asnjë mysafir në hotel", room: "Dhoma", guests: "Mysafirët", pax: "pax",
    balance: "Bilanci", charges: "Zërat", payments: "Pagesat", addCharge: "Shto zë",
    addPayment: "Shto pagesë", close: "Mbyll", passport: "Pashaporta", country: "Vendi",
    tourist_tax: "Taksë turistike (€)", notes: "Shënime", confirm: "Konfirmo regjistrimin", complete: "Përfundo largimin",
    charge: "Zë", payment: "Pagesë", item: "Emri", amount: "Shuma", method: "Mënyra",
    cash: "Para", card: "Kartë", transfer: "Transfer", beds24: "Beds24 (online)", save: "Ruaj",
    quickChargeDinner: "Darkë", quickChargeBreakfast: "Mëngjes", quickChargeTransfer: "Transfer aeroporti (€80)",
    quickChargeLaundry: "Lavanderi (€7)", quickChargeMinibar: "Mini-bar", quickChargeExtraBed: "Krevat shtesë",
    category: "Kategoria", extra: "Ekstra", food: "Ushqim & Pije", vat: "TVSH", vatBreakdown: "TVSH përmbledhje",
    totals: "Totali", checkedIn: "Regjistruar", checkedOut: "Larguar", reserved: "Rezervuar",
    initFolio: "Inicializo faturën për të filluar", cashDrawer: "Para sot", cardsToday: "Kartë sot",
    totalReceived: "Totali i marrë sot", chargesToday: "Zërat sot",
    exportEvizitor: "eVizitor CSV", downloadInvoice: "Fatura PDF",
  },
};

const useApiCall = () => useCallback((endpoint, options = {}) => apiCall(endpoint, options), []);

// Authenticated file download helper — used for eVizitor CSV & folio PDF.
const downloadWithAuth = async (path, filename) => {
  const token = localStorage.getItem('admin_token');
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) { alert(`Download failed (${res.status})`); return; }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

// -------- Sub-components --------

const StatusBadge = ({ folio, t }) => {
  const { reserved: rLabel, checkedIn, checkedOut } = t;
  if (!folio) return <span className="text-xs text-gray-400">{rLabel}</span>;
  const s = folio.status;
  const map = {
    reserved: { c: 'bg-gray-100 text-gray-700', label: rLabel },
    checked_in: { c: 'bg-green-100 text-green-700', label: checkedIn },
    checked_out: { c: 'bg-blue-100 text-blue-700', label: checkedOut },
    no_show: { c: 'bg-red-100 text-red-700', label: 'No-show' },
  };
  const cfg = map[s] || map.reserved;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cfg.c}`}>{cfg.label}</span>;
};

const GuestRow = ({ entry, kind, onOpen, t, apiCall, onRefresh }) => {
  const { pax } = t;
  const tags = entry.guest_tags || [];
  const isMasked = (entry.guest_name || '').toLowerCase() === 'guest' || !entry.guest_name;
  const [editing, setEditing] = React.useState(false);
  const [nameInput, setNameInput] = React.useState('');
  const [suggestions, setSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const debounceRef = React.useRef(null);
  React.useEffect(() => {
    if (!editing) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = nameInput.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const rid = entry.room_id != null ? `&room_id=${encodeURIComponent(entry.room_id)}` : '';
        const res = await apiCall(`/api/admin/reception/guests/search?q=${encodeURIComponent(q)}&limit=6${rid}`);
        setSuggestions(res.items || []);
        setShowSuggestions(true);
      } catch (_) { /* ignore autocomplete errors */ }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [nameInput, editing, apiCall]);
  const commitName = async (val) => {
    const n = (val ?? nameInput).trim();
    if (!n) return;
    try {
      await apiCall(`/api/admin/reception/bookings/${entry.booking_id}/real-name`, {
        method: 'PUT', body: JSON.stringify({ real_name: n }),
      });
      setEditing(false);
      setNameInput('');
      setSuggestions([]);
      setShowSuggestions(false);
      onRefresh && onRefresh();
    } catch (err) { alert(`Save name failed: ${err.message}`); }
  };
  const saveRealName = async (e) => {
    e && e.stopPropagation();
    await commitName();
  };
  return (
  <div
    className="flex items-center gap-3 p-3 hover:bg-orange-50 rounded-lg cursor-pointer transition-colors"
    onClick={() => onOpen(entry)}
    data-testid={`reception-guest-row-${entry.booking_id}`}
  >
    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
      kind === 'arrival' ? 'bg-green-100 text-green-600' :
      kind === 'departure' ? 'bg-blue-100 text-blue-600' :
      'bg-orange-100 text-orange-600'
    }`}>
      {kind === 'arrival' ? <LogIn className="w-5 h-5" /> :
        kind === 'departure' ? <LogOut className="w-5 h-5" /> : <Users className="w-5 h-5" />}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        {editing ? (
          <div onClick={(e) => e.stopPropagation()} className="relative flex items-center gap-1">
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveRealName(e)}
              onFocus={() => nameInput.trim().length >= 2 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder="Real name"
              autoFocus
              className="text-sm border rounded px-2 py-0.5 w-40"
              data-testid={`reception-realname-input-${entry.booking_id}`}
            />
            <button
              type="button"
              onClick={saveRealName}
              className="text-[10px] font-bold bg-orange-500 text-white px-2 py-0.5 rounded"
              data-testid={`reception-realname-save-${entry.booking_id}`}
            >Save</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(false); setSuggestions([]); }} className="text-[10px] text-gray-500">✕</button>
            {showSuggestions && suggestions.length > 0 && (
              <ul
                className="absolute z-50 top-full left-0 mt-1 w-60 bg-white border border-orange-200 rounded-lg shadow-lg max-h-56 overflow-y-auto"
                data-testid={`reception-realname-suggestions-${entry.booking_id}`}
              >
                {suggestions.map((s, i) => (
                  <li key={`${s.guest_name}-${i}`}>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); commitName(s.guest_name); }}
                      className="w-full text-left px-2 py-1 hover:bg-orange-50 flex items-center justify-between gap-2"
                      data-testid={`reception-realname-suggestion-${entry.booking_id}-${i}`}
                    >
                      <span className="text-xs font-semibold text-gray-900 truncate flex items-center gap-1">
                        {s.guest_name}
                        {s.matched_room && (
                          <span
                            className="text-[8px] font-bold uppercase tracking-wider bg-emerald-500 text-white px-1 py-0.5 rounded"
                            title="Previously stayed in this room type"
                          >
                            Same room
                          </span>
                        )}
                      </span>
                      {s.last_stay && (
                        <span className="text-[9px] text-gray-400 whitespace-nowrap">
                          {String(s.last_stay).slice(0, 10)}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <>
            <p className="font-semibold text-gray-900 truncate">{entry.guest_name || '—'}</p>
            {isMasked && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                className="text-[9px] font-bold uppercase tracking-wider bg-orange-100 text-orange-800 border border-orange-300 px-1.5 py-0.5 rounded hover:bg-orange-200"
                title="Attach the real guest name — enables per-person tags"
                data-testid={`reception-realname-set-${entry.booking_id}`}
              >
                + Real name
              </button>
            )}
            {entry.masked_name && (
              <span className="text-[9px] text-gray-400 italic" title="Beds24 name was masked">
                (was "{entry.masked_name}")
              </span>
            )}
          </>
        )}
        <StatusBadge folio={entry.folio} t={t} />
        {tags.map((tg, i) => {
          const cls = {
            vip:      'bg-amber-500 text-amber-950',
            quiet:    'bg-sky-500 text-white',
            allergic: 'bg-red-500 text-white',
            late:     'bg-purple-500 text-white',
            pillow:   'bg-indigo-500 text-white',
            note:     'bg-slate-600 text-white',
          }[tg.kind] || 'bg-slate-600 text-white';
          return (
            <span
              key={i}
              className={`text-[9px] font-bold uppercase tracking-wider ${cls} px-1.5 py-0.5 rounded`}
              data-testid={`reception-tag-${entry.booking_id}-${i}`}
              title={`Guest tag · ${tg.kind}`}
            >
              {tg.label}
            </span>
          );
        })}
      </div>
      <p className="text-xs text-gray-500 truncate">
        {entry.room_name} · {entry.adults + entry.children} {pax} · {entry.check_in} → {entry.check_out}
      </p>
    </div>
    {entry.folio?.balance > 0 && (
      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-semibold">
        €{entry.folio.balance.toFixed(2)}
      </span>
    )}
  </div>
  );
};

// -------- Folio modal --------

const QUICK_CHARGES = (t) => ([
  { icon: Coffee, key: 'quickChargeBreakfast', amount: 8, category: 'food_beverage', vat: 0.07 },
  { icon: Utensils, key: 'quickChargeDinner', amount: 15, category: 'food_beverage', vat: 0.07 },
  { icon: Car, key: 'quickChargeTransfer', amount: 80, category: 'extra', vat: 0.21 },
  { icon: Sparkles, key: 'quickChargeLaundry', amount: 7, category: 'extra', vat: 0.21 },
  { icon: Wallet, key: 'quickChargeMinibar', amount: 5, category: 'food_beverage', vat: 0.21 },
  { icon: Plus, key: 'quickChargeExtraBed', amount: 20, category: 'extra', vat: 0.21 },
]);

const FolioModal = ({ entry, t, onClose, onRefresh, userRole }) => {
  const apiCall = useApiCall();
  const [folio, setFolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ci, setCi] = useState({ passport_number: '', passport_country: '', tourist_tax_collected: 0, notes: '' });
  const [pay, setPay] = useState({ amount: 0, method: 'cash', note: '' });
  const [manual, setManual] = useState({ item: '', amount: 0, category: 'extra', vat_rate: 0.21 });
  const [taxQuote, setTaxQuote] = useState(null);
  const [moving, setMoving] = useState(false);
  const [messaging, setMessaging] = useState(false);
  const [scanning, setScanning] = useState(false);
  const quickCharges = QUICK_CHARGES(t);

  useEffect(() => {
    if (!folio || folio.status !== 'reserved') return;
    const nights = Math.max(1, Math.round((new Date(folio.check_out) - new Date(folio.check_in)) / 86400000) || 1);
    apiCall(`/api/admin/finance/tourist-tax/quote?adults=${folio.adults ?? 2}&children=${folio.children ?? 0}&nights=${nights}`)
      .then(setTaxQuote).catch(() => setTaxQuote(null));
  }, [folio, apiCall]);

  const setNoShow = async (undo) => {
    setBusy(true);
    try {
      const f = await apiCall(`/api/admin/finance/no-show/${entry.booking_id}`, { method: undo ? 'DELETE' : 'POST' });
      setFolio(f); onRefresh?.();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const f = await apiCall(`/api/admin/reception/folios/${entry.booking_id}`).catch(() => null);
      if (!f) {
        // Initialise from Beds24 booking info
        const seed = {
          guest_name: entry.guest_name, room_id: entry.room_id, room_name: entry.room_name,
          check_in: entry.check_in, check_out: entry.check_out,
          adults: entry.adults, children: entry.children, unit_id: entry.unit_id ?? null,
          phone: entry.phone || '', email: entry.email || '',
        };
        const created = await apiCall(`/api/admin/reception/folios/${entry.booking_id}/init`, {
          method: 'POST', body: JSON.stringify(seed),
        });
        setFolio(created);
      } else {
        setFolio(f);
      }
    } finally { setLoading(false); }
  }, [apiCall, entry]);
  useEffect(() => { load(); }, [load]);

  const doCheckIn = async () => {
    setBusy(true);
    try {
      const f = await apiCall(`/api/admin/reception/folios/${entry.booking_id}/check-in`, {
        method: 'PUT', body: JSON.stringify(ci),
      });
      setFolio(f); onRefresh?.();
    } finally { setBusy(false); }
  };
  const doCheckOut = async () => {
    setBusy(true);
    try {
      const f = await apiCall(`/api/admin/reception/folios/${entry.booking_id}/check-out`, { method: 'PUT' });
      setFolio(f); onRefresh?.();
    } finally { setBusy(false); }
  };
  const addCharge = async (charge) => {
    setBusy(true);
    try {
      const f = await apiCall(`/api/admin/reception/folios/${entry.booking_id}/charges`, {
        method: 'POST', body: JSON.stringify(charge),
      });
      setFolio(f); onRefresh?.();
    } finally { setBusy(false); }
  };
  const addPayment = async () => {
    if (!pay.amount) return;
    setBusy(true);
    try {
      const f = await apiCall(`/api/admin/reception/folios/${entry.booking_id}/payments`, {
        method: 'POST', body: JSON.stringify(pay),
      });
      setFolio(f); setPay({ amount: 0, method: 'cash', note: '' }); onRefresh?.();
    } finally { setBusy(false); }
  };
  const removeCharge = async (chargeId) => {
    setBusy(true);
    try {
      const f = await apiCall(`/api/admin/reception/folios/${entry.booking_id}/charges/${chargeId}`, { method: 'DELETE' });
      setFolio(f); onRefresh?.();
    } finally { setBusy(false); }
  };

  const status = folio?.status || 'reserved';
  const {
    passport: L_passport, country: L_country, tourist_tax: L_tax, notes: L_notes,
    checkIn: L_ci, confirm: L_confirm, complete: L_complete, addCharge: L_addCharge,
    item: L_item, addPayment: L_addPayment, cash: L_cash, card: L_card, transfer: L_transfer,
    beds24: L_beds24, save: L_save, balance: L_balance, charges: L_charges,
  } = t;
  const chargeList = folio?.charges || [];
  const paymentList = folio?.payments || [];
  const folioBalance = folio?.balance || 0;
  const balanceCls = folioBalance > 0 ? 'bg-yellow-50 border border-yellow-200'
    : folioBalance < 0 ? 'bg-red-50 border border-red-200'
    : 'bg-green-50 border border-green-200';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="reception-folio-modal">
        <div className="p-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg">{entry.guest_name}</h3>
            <p className="text-xs text-orange-50">{entry.room_name} · {entry.check_in} → {entry.check_out} · #{entry.booking_id}</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={async () => {
                try {
                  const d = await apiCall(`/api/admin/reception/folios/${entry.booking_id}/checkin-link`, { method: 'POST' });
                  await navigator.clipboard?.writeText(d.url);
                  alert(`Self check-in link copied:\n${d.url}${d.completed ? '\n\n✓ Guest already completed online check-in.' : '\n\nSend it via "Message" → "Online check-in" template.'}`);
                } catch (e) { alert(e.message); }
              }}
              className="p-2 hover:bg-white/20 rounded-lg flex items-center gap-1 text-sm"
              title="Copy the guest's self check-in link"
              data-testid="reception-checkin-link"
            >
              <Smartphone className="w-4 h-4" /> Self check-in
            </button>
            <button
              onClick={() => downloadWithAuth(`/api/admin/reception/folios/${entry.booking_id}/registration-card.pdf`, `registration-card-${entry.booking_id}.pdf`)}
              className="p-2 hover:bg-white/20 rounded-lg flex items-center gap-1 text-sm"
              title="Print pre-filled registration card for the guest to sign"
              data-testid="reception-registration-card"
            >
              <FileText className="w-4 h-4" /> Reg. card
            </button>
            <button
              onClick={() => setMessaging(true)}
              className="p-2 hover:bg-white/20 rounded-lg flex items-center gap-1 text-sm"
              title="Send WhatsApp / SMS to the guest"
              data-testid="reception-message-guest"
            >
              <MessageCircle className="w-4 h-4" /> Message
            </button>
            {folio && !folio.invoice_number && status === 'checked_in' && (
              <button
                onClick={() => downloadWithAuth(`/api/admin/reception/folios/${entry.booking_id}/invoice.pdf?issue=1`, `invoice_${entry.booking_id}.pdf`).then(() => load())}
                className="p-2 hover:bg-white/20 rounded-lg flex items-center gap-1 text-sm"
                title="Issue final numbered invoice now (guest paying before check-out)"
                data-testid="reception-folio-issue-invoice"
              >
                <FileText className="w-4 h-4" /> Issue invoice
              </button>
            )}
            <button
              onClick={() => downloadWithAuth(`/api/admin/reception/folios/${entry.booking_id}/invoice.pdf`, `invoice_${entry.booking_id}.pdf`).then(() => load())}
              className="p-2 hover:bg-white/20 rounded-lg flex items-center gap-1 text-sm"
              title={folio?.invoice_number || status === 'checked_out' ? t.downloadInvoice : 'Proforma (no invoice number until check-out)'}
              data-testid="reception-folio-invoice"
            >
              <FileText className="w-4 h-4" /> {folio?.invoice_number || status === 'checked_out' ? 'Invoice PDF' : 'Proforma PDF'}
            </button>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="reception-folio-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : (
          <div className="p-5 overflow-y-auto max-h-[calc(90vh-70px)] space-y-4">
            <div className={`rounded-lg p-3 flex items-center justify-between ${balanceCls}`}>
              <div>
                <p className="text-xs text-gray-600">{L_balance}</p>
                <p className="text-2xl font-bold text-gray-900">€{folioBalance.toFixed(2)}</p>
              </div>
              <StatusBadge folio={folio} t={t} />
            </div>

            {status === 'reserved' && (
              <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm flex items-center gap-2"><LogIn className="w-4 h-4" /> {L_ci}</h4>
                  {!scanning && (
                    <button onClick={() => setScanning(true)} className="text-xs font-semibold text-sky-700 hover:underline flex items-center gap-1" data-testid="reception-ci-scan-id">
                      <Camera className="w-3.5 h-3.5" /> Scan passport / ID
                    </button>
                  )}
                </div>
                {scanning && (
                  <IdScanPanel
                    bookingId={entry.booking_id}
                    onClose={() => setScanning(false)}
                    onApplied={(f, fields) => {
                      setFolio(f);
                      setCi({ ...ci, passport_number: fields.document_number || ci.passport_number, passport_country: fields.nationality || fields.issuing_country || ci.passport_country });
                      setScanning(false);
                      onRefresh?.();
                    }}
                  />
                )}
                {folio?.precheckin?.completed_at && (
              <p className="text-[11px] text-emerald-700 font-semibold" data-testid="reception-precheckin-badge">✓ Online check-in completed {folio.precheckin.completed_at.slice(0, 10)}{folio.precheckin.eta ? ` · ETA ${folio.precheckin.eta}` : ''}{folio.precheckin.signature_url ? ' · signed' : ''}</p>
            )}
            {folio?.guest_doc && !scanning && (
                  <p className="text-[11px] text-slate-500" data-testid="reception-ci-doc-summary">
                    ID on file: {folio.guest_doc.document_type || 'document'} {folio.guest_doc.document_number || ''} · {folio.guest_doc.nationality || folio.guest_doc.issuing_country || ''} · DOB {folio.guest_doc.date_of_birth || '—'}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <input className="px-3 py-2 border rounded text-sm" placeholder={L_passport} value={ci.passport_number} onChange={(e) => setCi({ ...ci, passport_number: e.target.value })} data-testid="reception-ci-passport" />
                  <input className="px-3 py-2 border rounded text-sm" placeholder={L_country} value={ci.passport_country} onChange={(e) => setCi({ ...ci, passport_country: e.target.value })} />
                  <input type="number" step="0.01" className="px-3 py-2 border rounded text-sm" placeholder={L_tax} value={ci.tourist_tax_collected} onChange={(e) => setCi({ ...ci, tourist_tax_collected: parseFloat(e.target.value) || 0 })} data-testid="reception-ci-tax" />
                  <input className="px-3 py-2 border rounded text-sm" placeholder={L_notes} value={ci.notes} onChange={(e) => setCi({ ...ci, notes: e.target.value })} />
                </div>
                {taxQuote && taxQuote.amount > 0 && (
                  <div className="flex items-center justify-between text-xs bg-white border border-dashed rounded px-3 py-2" data-testid="reception-ci-tax-quote">
                    <span className="text-gray-600">
                      Suggested tourist tax: <b>€{taxQuote.amount.toFixed(2)}</b>
                      {' '}({taxQuote.adults} × {taxQuote.nights}n × €{taxQuote.adult_rate.toFixed(2)}
                      {taxQuote.children > 0 ? ` + ${taxQuote.children} child × €${taxQuote.child_rate.toFixed(2)}` : ''})
                    </span>
                    <button onClick={() => setCi({ ...ci, tourist_tax_collected: taxQuote.amount })} className="text-orange-600 font-semibold hover:underline" data-testid="reception-ci-tax-use">Use</button>
                  </div>
                )}
                <button onClick={doCheckIn} disabled={busy} className="w-full bg-green-500 text-white py-2 rounded font-semibold hover:bg-green-600 disabled:opacity-50" data-testid="reception-ci-confirm">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : L_confirm}
                </button>
                <button onClick={() => setNoShow(false)} disabled={busy} className="w-full text-xs text-red-600 hover:underline" data-testid="reception-mark-no-show">
                  Guest never arrived — mark as no-show
                </button>
              </div>
            )}
            {status === 'no_show' && (
              <div className="border border-red-200 bg-red-50 rounded-lg p-3 flex items-center justify-between text-sm" data-testid="reception-no-show-box">
                <span className="text-red-700 font-semibold">Marked no-show{folio?.no_show_at ? ` · ${folio.no_show_at.slice(0, 16).replace('T', ' ')}` : ''}</span>
                <button onClick={() => setNoShow(true)} disabled={busy} className="text-xs text-gray-700 underline" data-testid="reception-undo-no-show">Undo</button>
              </div>
            )}
            {folio?.invoice_number && (
              <p className="text-xs text-gray-500" data-testid="reception-invoice-number">Invoice No. <b className="text-gray-800">{folio.invoice_number}</b> · issued {(folio.invoice_issued_at || '').slice(0, 10)}</p>
            )}
            {(status === 'checked_in' || status === 'reserved') && <FolioMemory bookingId={entry.booking_id} userRole={userRole} />}
            {(status === 'checked_in' || status === 'reserved') && <FolioGuests bookingId={entry.booking_id} />}
            {(status === 'checked_in' || status === 'reserved') && (
              <div className="flex items-center justify-between text-xs bg-slate-50 border rounded-lg px-3 py-2" data-testid="reception-room-line">
                <span className="text-slate-600">
                  Room: <b className="text-slate-900">{folio?.room_name || entry.room_name}</b>
                  {folio?.unit_id != null && <span className="text-slate-500"> · unit {folio.unit_id}</span>}
                  {(folio?.moves || []).length > 0 && (
                    <span className="block text-[11px] text-slate-400 mt-0.5" data-testid="reception-move-history">
                      Moved {(folio.moves || []).map((m) => `${m.from?.unit_label || m.from?.room_name || '?'} → ${m.to?.unit_label || m.to?.room_name}`).join(', ')}
                    </span>
                  )}
                </span>
                <button onClick={() => setMoving(true)} className="px-2 py-1 rounded border border-slate-300 hover:bg-white font-semibold text-slate-700" data-testid="reception-move-room">
                  Move room
                </button>
              </div>
            )}
            {status === 'checked_in' && (
              <button onClick={doCheckOut} disabled={busy} className="w-full bg-blue-500 text-white py-2 rounded font-semibold hover:bg-blue-600 disabled:opacity-50" data-testid="reception-co-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : L_complete}
              </button>
            )}

            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-sm mb-3">{L_addCharge}</h4>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {quickCharges.map((q) => {
                  const Icon = q.icon;
                  return (
                    <button key={q.key} onClick={() => addCharge({ item: t[q.key], amount: q.amount, category: q.category, vat_rate: q.vat, quantity: 1 })}
                      disabled={busy}
                      className="flex flex-col items-center gap-1 p-2 border rounded hover:bg-orange-50 text-xs disabled:opacity-50"
                      data-testid={`reception-quick-${q.key}`}>
                      <Icon className="w-4 h-4 text-orange-500" />
                      <span>{t[q.key]}</span>
                      <span className="text-gray-500">€{q.amount}</span>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-3 gap-2 items-center">
                <input className="px-2 py-1.5 border rounded text-xs" placeholder={L_item} value={manual.item} onChange={(e) => setManual({ ...manual, item: e.target.value })} />
                <input type="number" step="0.01" className="px-2 py-1.5 border rounded text-xs" placeholder="€" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: parseFloat(e.target.value) || 0 })} />
                <button
                  onClick={() => { if (manual.item && manual.amount) { addCharge({ ...manual, quantity: 1 }); setManual({ item: '', amount: 0, category: 'extra', vat_rate: 0.21 }); } }}
                  disabled={busy || !manual.item || !manual.amount}
                  className="bg-orange-500 text-white py-1.5 rounded text-xs font-semibold disabled:opacity-50">+</button>
              </div>
            </div>

            {chargeList.length > 0 && (
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2">{L_charges}</h4>
                <ul className="divide-y">
                  {chargeList.map((c) => {
                    const total = c.amount * c.quantity;
                    const vatPct = (c.vat_rate * 100).toFixed(0);
                    return (
                    <li key={c.id} className="flex justify-between items-center py-1.5 text-sm">
                      <div>
                        <span className="text-gray-900">{c.item}</span>
                        <span className="text-xs text-gray-400 ml-2">×{c.quantity} · VAT {vatPct}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">€{total.toFixed(2)}</span>
                        <button onClick={() => removeCharge(c.id)} disabled={busy} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                      </div>
                    </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-sm mb-3">{L_addPayment}</h4>
              <div className="grid grid-cols-3 gap-2 items-center">
                <input type="number" step="0.01" className="px-3 py-2 border rounded text-sm" placeholder="€" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: parseFloat(e.target.value) || 0 })} data-testid="reception-pay-amount" />
                <select className="px-3 py-2 border rounded text-sm" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
                  <option value="cash">{L_cash}</option>
                  <option value="card">{L_card}</option>
                  <option value="transfer">{L_transfer}</option>
                  <option value="beds24">{L_beds24}</option>
                </select>
                <button onClick={addPayment} disabled={busy || !pay.amount} className="bg-orange-500 text-white py-2 rounded font-semibold disabled:opacity-50" data-testid="reception-pay-save">{L_save}</button>
              </div>
              {paymentList.length > 0 && (
                <ul className="mt-3 divide-y text-sm">
                  {paymentList.map((p) => {
                    const methodLabel = t[p.method] || p.method;
                    return (
                    <li key={p.id} className="flex justify-between py-1.5">
                      <span className="text-gray-700">{methodLabel}</span>
                      <span className="font-mono text-green-600">-€{p.amount.toFixed(2)}</span>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
      {moving && (
        <RoomMoveModal bookingId={entry.booking_id} guestName={entry.guest_name} onClose={() => setMoving(false)}
          onMoved={(f) => { setFolio(f); onRefresh?.(); }} />
      )}
      {messaging && (
        <GuestMessageModal bookingId={entry.booking_id} guestName={entry.guest_name} onClose={() => { setMessaging(false); load(); }} />
      )}
    </div>
  );
};

// -------- Main dashboard --------

const ReceptionDashboard = ({ userRole, focusBookingId, onFocusHandled, onNavigate }) => {
  const apiCall = useApiCall();
  const [uiLang, setUiLang] = useState(() => localStorage.getItem('reception_lang') || 'en');
  const t = T[uiLang] || T.en;
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [openEntry, setOpenEntry] = useState(null);
  const [showLostFound, setShowLostFound] = useState(false);
  const [showEvizitor, setShowEvizitor] = useState(false);
  const [evizitorIssues, setEvizitorIssues] = useState(null);
  useEffect(() => {
    if (!board?.date) return;
    let alive = true;
    fetchEvizitorPrecheck(board.date).then((d) => alive && setEvizitorIssues(d.issues)).catch(() => {});
    return () => { alive = false; };
  }, [board?.date, board?.arrivals?.length]);
  const downloadEvizitor = () => downloadWithAuth(`/api/admin/reception/export/evizitor?date_str=${board?.date || ''}`, `evizitor_${board?.date || 'today'}.csv`);
  const [tagFilter, setTagFilter] = useState(''); // '' | tag label (case-insensitive)

  // Global search (Ctrl+K) deep-link: open the folio for a booking id.
  useEffect(() => {
    if (!focusBookingId || loading) return;
    const all = [...(board?.arrivals || []), ...(board?.departures || []), ...(board?.in_house || [])];
    const hit = all.find((e) => e.booking_id === focusBookingId);
    if (hit) { setOpenEntry(hit); onFocusHandled?.(); return; }
    apiCall(`/api/admin/reception/folios/${focusBookingId}`)
      .then((f) => setOpenEntry({
        booking_id: f.booking_id, guest_name: f.guest_name, room_id: f.room_id, unit_id: f.unit_id,
        room_name: f.room_name, check_in: f.check_in, check_out: f.check_out, adults: f.adults, children: f.children,
      }))
      .catch(() => alert(`No folio found for booking ${focusBookingId}`))
      .finally(() => onFocusHandled?.());
  }, [focusBookingId, board, loading, apiCall, onFocusHandled]);

  // Collect all unique tag labels present in TODAY's arrivals (case-insensitive)
  // with a small hint about which "kind" so we can color the filter chip.
  const arrivalTags = useMemo(() => {
    const seen = new Map();
    for (const e of board?.arrivals || []) {
      for (const t of e.guest_tags || []) {
        const key = (t.label || '').toLowerCase();
        if (!key) continue;
        if (!seen.has(key)) seen.set(key, { label: t.label, kind: t.kind, count: 0 });
        seen.get(key).count += 1;
      }
    }
    return [...seen.values()].sort((a, b) => b.count - a.count);
  }, [board]);

  const matchesFilter = useCallback((e) => {
    if (!tagFilter) return true;
    const f = tagFilter.toLowerCase();
    return (e.guest_tags || []).some((tg) => (tg.label || '').toLowerCase() === f);
  }, [tagFilter]);

  const filteredArrivals = useMemo(
    () => (board?.arrivals || []).filter(matchesFilter),
    [board, matchesFilter],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([
        apiCall('/api/admin/reception/today'),
        apiCall('/api/admin/reception/daily-revenue'),
      ]);
      setBoard(b); setRevenue(r);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [apiCall]);
  useEffect(() => { refresh(); }, [refresh]);

  const cash = revenue?.payments_by_method?.cash || 0;
  const card = revenue?.payments_by_method?.card || 0;
  const totalPaid = revenue?.total_payments || 0;
  const totalCharges = revenue?.total_charges || 0;
  const {
    today: L_today, refresh: L_refresh, arrivals: L_arrivals, departures: L_departures,
    inHouse: L_inHouse, noArrivals, noDepartures, noInHouse,
    chargesToday, totalReceived, cashDrawer, cardsToday,
  } = t;
  const totalChargesStr = `€${totalCharges.toFixed(2)}`;
  const totalPaidStr = `€${totalPaid.toFixed(2)}`;
  const cashStr = `€${cash.toFixed(2)}`;
  const cardStr = `€${card.toFixed(2)}`;

  return (
    <div className="space-y-6" data-testid="reception-dashboard">
      <CashFloatBanner onNavigate={onNavigate} />
      <BriefingCard userRole={userRole} onOpenTab={(tab) => onNavigate?.({ tab })} />
      <ShiftPanel userRole={userRole} />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-orange-500" /> {L_today}
          </h2>
          <p className="text-sm text-gray-500 mt-1">{board?.date}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <PushNotificationButton role="reception" lang={uiLang} />
          <select value={uiLang} onChange={(e) => { setUiLang(e.target.value); localStorage.setItem('reception_lang', e.target.value); }} className="px-3 py-1.5 border rounded text-sm" data-testid="reception-lang-picker">
            <option value="en">EN</option>
            <option value="me">ME</option>
            <option value="sq">SQ</option>
          </select>
          <button onClick={() => setShowEvizitor(true)} className="relative flex items-center gap-2 bg-white border px-3 py-1.5 rounded text-sm hover:bg-gray-50" data-testid="reception-export-evizitor">
            <Download className="w-4 h-4" /> {t.exportEvizitor}
            {evizitorIssues > 0 && <span className="absolute -top-2 -right-2 min-w-[1.25rem] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center" title={`${evizitorIssues} eVizitor issue(s)`} data-testid="reception-evizitor-badge">{evizitorIssues}</span>}
            {evizitorIssues === 0 && <Check className="w-3.5 h-3.5 text-emerald-600" data-testid="reception-evizitor-ok" />}
          </button>
          <button onClick={() => setShowLostFound(true)} className="flex items-center gap-2 bg-white border px-3 py-1.5 rounded text-sm hover:bg-gray-50" data-testid="reception-lost-found">
            <Package className="w-4 h-4" /> Lost &amp; Found
          </button>
          <button onClick={refresh} disabled={loading} className="flex items-center gap-2 bg-white border px-3 py-1.5 rounded text-sm hover:bg-gray-50" data-testid="reception-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {L_refresh}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={TrendingUp} label={chargesToday} value={totalChargesStr} color="orange" testid="reception-kpi-charges" />
        <KPI icon={Wallet} label={totalReceived} value={totalPaidStr} color="green" testid="reception-kpi-received" />
        <KPI icon={DollarSign} label={cashDrawer} value={cashStr} color="blue" testid="reception-kpi-cash" />
        <KPI icon={DollarSign} label={cardsToday} value={cardStr} color="purple" testid="reception-kpi-card" />
      </div>

      <Beds24Feed lang={uiLang} />

      <AutoMessagesCard />

      <GuestMemoryCard userRole={userRole} />

      <TillCard apiCall={apiCall} onRefresh={refresh} uiLang={uiLang} />

      {/* Tag filter — front desk narrows today's arrivals to a specific tag */}
      {arrivalTags.length > 0 && (
        <div className="bg-white rounded-xl border p-3" data-testid="reception-tag-filter">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">
            Filter arrivals by tag
          </p>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setTagFilter('')}
              className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${
                tagFilter === '' ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid="reception-tag-filter-all"
            >
              All · {board?.arrivals?.length || 0}
            </button>
            {arrivalTags.map((tg) => {
              const active = tagFilter.toLowerCase() === tg.label.toLowerCase();
              const cls = {
                vip:      active ? 'bg-amber-500 text-amber-950 border-amber-600' : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100',
                quiet:    active ? 'bg-sky-500 text-white border-sky-600' : 'bg-sky-50 text-sky-800 border-sky-300 hover:bg-sky-100',
                allergic: active ? 'bg-red-500 text-white border-red-600' : 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100',
                late:     active ? 'bg-purple-500 text-white border-purple-600' : 'bg-purple-50 text-purple-800 border-purple-300 hover:bg-purple-100',
                pillow:   active ? 'bg-indigo-500 text-white border-indigo-600' : 'bg-indigo-50 text-indigo-800 border-indigo-300 hover:bg-indigo-100',
                note:     active ? 'bg-slate-600 text-white border-slate-700' : 'bg-slate-50 text-slate-800 border-slate-300 hover:bg-slate-100',
              }[tg.kind] || (active ? 'bg-slate-600 text-white border-slate-700' : 'bg-slate-50 text-slate-800 border-slate-300 hover:bg-slate-100');
              return (
                <button
                  key={tg.label}
                  type="button"
                  onClick={() => setTagFilter(active ? '' : tg.label)}
                  className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${cls}`}
                  data-testid={`reception-tag-filter-${tg.label}`}
                >
                  {tg.label} · {tg.count}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <Column
          title={L_arrivals} count={filteredArrivals.length}
          items={filteredArrivals} kind="arrival"
          empty={tagFilter ? `No arrivals with tag "${tagFilter}"` : noArrivals} onOpen={setOpenEntry} t={t}
          testid="reception-col-arrivals"
          apiCall={apiCall} onRefresh={refresh}
        />
        <Column
          title={L_departures} count={board?.counts?.departures || 0}
          items={board?.departures || []} kind="departure"
          empty={noDepartures} onOpen={setOpenEntry} t={t}
          testid="reception-col-departures"
          apiCall={apiCall} onRefresh={refresh}
        />
        <Column
          title={L_inHouse} count={board?.counts?.in_house || 0}
          items={board?.in_house || []} kind="inhouse"
          empty={noInHouse} onOpen={setOpenEntry} t={t}
          testid="reception-col-inhouse"
          apiCall={apiCall} onRefresh={refresh}
        />
      </div>

      {showLostFound && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowLostFound(false)}>
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl p-4" onClick={(e) => e.stopPropagation()} data-testid="reception-lost-found-modal">
            <div className="flex justify-end"><button onClick={() => setShowLostFound(false)} className="p-1 rounded hover:bg-gray-100" data-testid="reception-lost-found-close"><X className="w-5 h-5" /></button></div>
            <LostFoundPanel userRole={userRole} />
          </div>
        </div>
      )}
      {openEntry && (
        <FolioModal entry={openEntry} t={t} onClose={() => setOpenEntry(null)} onRefresh={refresh} userRole={userRole} />
      )}
      {showEvizitor && (
        <EvizitorPrecheckModal
          dateStr={board?.date || ''}
          onClose={() => setShowEvizitor(false)}
          onDownload={() => { downloadEvizitor(); setShowEvizitor(false); }}
          onOpenFolio={(b) => {
            const all = [...(board?.arrivals || []), ...(board?.departures || []), ...(board?.in_house || [])];
            const hit = all.find((e) => e.booking_id === b.booking_id);
            setShowEvizitor(false);
            if (hit) setOpenEntry(hit);
            else setOpenEntry({ booking_id: b.booking_id, guest_name: b.guest_name, room_name: b.room_name, check_in: board?.date });
          }}
        />
      )}
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, color, testid }) => (
  <div className="bg-white rounded-xl border p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
      <Icon className={`w-4 h-4 text-${color}-500`} /> {label}
    </div>
    <p className="text-xl font-bold text-gray-900">{value}</p>
  </div>
);

// Cash Till Reconciliation — start-of-shift float, live cash tracking, end-of-shift count with discrepancy.
const TillLabels = {
  en: { title: "Cash Till", open: "Open till", close: "Close till", opening: "Opening float", expected: "Expected", counted: "Counted", cash: "Cash received today", discrepancy: "Discrepancy", closed: "Closed", cancel: "Cancel", confirm: "Confirm" },
  me: { title: "Kasa", open: "Otvori kasu", close: "Zatvori kasu", opening: "Početno stanje", expected: "Očekivano", counted: "Prebrojano", cash: "Gotovina danas", discrepancy: "Odstupanje", closed: "Zatvoreno", cancel: "Otkaži", confirm: "Potvrdi" },
  sq: { title: "Arka", open: "Hap arkën", close: "Mbyll arkën", opening: "Fondi hapës", expected: "Pritur", counted: "Numëruar", cash: "Para sot", discrepancy: "Diferenca", closed: "Mbyllur", cancel: "Anulo", confirm: "Konfirmo" },
};

const TillCard = ({ apiCall, onRefresh, uiLang }) => {
  const L = TillLabels[uiLang] || TillLabels.en;
  const [till, setTill] = useState(null);
  const [modal, setModal] = useState(null); // 'open' | 'close' | null
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    try { setTill(await apiCall('/api/admin/reception/till/summary')); } catch (e) { console.error(e); }
  }, [apiCall]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await apiCall('/api/admin/reception/till/open', { method: 'POST', body: JSON.stringify({ opening_float: amount, notes }) });
      setModal(null); setAmount(0); setNotes(''); load(); onRefresh?.();
    } catch (e) { alert(e.message); }
  };

  if (!till) return null;
  const { opened, closed, opening_float, cash_received_today, withdrawn_today, expected_closing, counted_cash, discrepancy, shift } = till;
  const wd = shift?.withdrawal;
  const discCls = discrepancy === null ? '' : discrepancy === 0 ? 'text-green-600' : (discrepancy > 0 ? 'text-blue-600' : 'text-red-600');

  return (
    <div className="bg-white rounded-xl border p-4" data-testid="reception-till-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2"><Wallet className="w-5 h-5 text-orange-500" /> {L.title}</h3>
        {!opened && (
          <button onClick={() => { setModal('open'); setAmount(50); }} className="text-sm bg-orange-500 text-white px-3 py-1.5 rounded font-semibold hover:bg-orange-600" data-testid="reception-till-open-btn">{L.open}</button>
        )}
        {opened && !closed && (
          <button onClick={() => setModal('close')} className="text-sm bg-blue-500 text-white px-3 py-1.5 rounded font-semibold hover:bg-blue-600" data-testid="reception-till-close-btn">{L.close}</button>
        )}
        {closed && <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-semibold">{L.closed}</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
        <div><p className="text-xs text-gray-500">{L.opening}</p><p className="font-semibold">€{opening_float.toFixed(2)}</p></div>
        <div><p className="text-xs text-gray-500">{L.cash}</p><p className="font-semibold">€{cash_received_today.toFixed(2)}</p></div>
        <div><p className="text-xs text-gray-500">{L.expected}</p><p className="font-semibold">€{expected_closing.toFixed(2)}{withdrawn_today > 0 && <span className="text-[10px] text-slate-500 font-normal"> · −€{withdrawn_today.toFixed(2)} withdrawn</span>}</p></div>
        <div><p className="text-xs text-gray-500">{L.counted}</p><p className="font-semibold">{counted_cash === null ? '—' : `€${counted_cash.toFixed(2)}`}</p></div>
        <div><p className="text-xs text-gray-500">{L.discrepancy}</p><p className={`font-semibold ${discCls}`}>{discrepancy === null ? '—' : `€${discrepancy.toFixed(2)}`}</p></div>
      </div>

      {closed && wd && (
        <div className={`mt-2 text-xs rounded px-2 py-1 ${wd.status === 'ok' ? 'bg-emerald-50 text-emerald-800' : wd.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-600'}`} data-testid="reception-till-withdrawal">
          {wd.status === 'ok' ? <>Withdrawal €{Number(wd.amount).toFixed(2)} registered · FCDC <span className="font-mono">{String(wd.fcdc).slice(0, 8)}…</span></>
            : wd.status === 'failed' ? <>Withdrawal €{Number(wd.amount).toFixed(2)} failed at EFI — retry in Finance → Fiscal Day</> : <>No withdrawal registered — {wd.reason}</>}
        </div>
      )}
      {modal === 'close' && <ShiftCloseModal onClose={() => setModal(null)} onDone={() => { load(); onRefresh?.(); }} />}
      {modal === 'open' && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="reception-till-modal">
            <h3 className="font-bold mb-3">{modal === 'open' ? L.open : L.close}</h3>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 border rounded text-sm mb-2" data-testid="reception-till-amount" />
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" className="w-full px-3 py-2 border rounded text-sm mb-3" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">{L.cancel}</button>
              <button onClick={submit} className="px-4 py-2 bg-orange-500 text-white rounded font-semibold text-sm" data-testid="reception-till-submit">{L.confirm}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Column = ({ title, count, items, kind, empty, onOpen, t, testid, apiCall, onRefresh }) => (
  <div className="bg-white rounded-xl border overflow-hidden" data-testid={testid}>
    <div className="p-3 border-b bg-gray-50 flex justify-between items-center">
      <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">{count}</span>
    </div>
    <div className="max-h-[500px] overflow-y-auto p-2">
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 p-3 text-center flex items-center justify-center gap-2">
          <AlertCircle className="w-4 h-4" /> {empty}
        </p>
      ) : (
        items.map((e) => (
          <GuestRow key={e.booking_id} entry={e} kind={kind} onOpen={onOpen} t={t} apiCall={apiCall} onRefresh={onRefresh} />
        ))
      )}
    </div>
  </div>
);

export default ReceptionDashboard;
